import React, { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Calendar,
  ExternalLink,
  Globe,
  MapPin,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { usePageSeo } from '../hooks/usePageSeo';
import AutomobileBrandLogo from '../components/automobile/AutomobileBrandLogo';
import AutomobileModelCard from '../components/automobile/AutomobileModelCard';
import { useAutomobileBrandData } from '../hooks/useAutomobileCatalogData';
import {
  AUTOMOBILE_BODY_TYPE_LABELS,
  AUTOMOBILE_BODY_TYPE_ORDER,
  AUTOMOBILE_BODY_CHIP,
  AUTOMOBILE_COMPANY_STATUS_LABELS,
  formatAutomobileCountry,
  type AutomobileBodyType,
} from '../types/automobile';
import { filterModelsForBodyFilter } from '../utils/automobileBodyFilter';
import {
  getAutomobileModelGroupKey,
  getAutomobileModelKey,
  normalizeAutomobileModelDisplayName,
} from '../utils/automobileModelDisplay';

interface Props {
  darkMode: boolean;
}

const AutomobileBrandPage: React.FC<Props> = ({ darkMode }) => {
  const { slug = '' } = useParams();
  const { brand, loading, error, catalogReady } = useAutomobileBrandData(slug);
  const [bodyFilter, setBodyFilter] = useState<AutomobileBodyType | ''>('');

  const filteredModels = useMemo(() => {
    if (!brand) return [];
    return filterModelsForBodyFilter(brand.models, bodyFilter);
  }, [brand, bodyFilter]);

  const groupedModels = useMemo(() => {
    if (!brand) return [];
    const order: string[] = [];
    const groups = new Map<string, typeof filteredModels>();
    for (const m of filteredModels) {
      const key = getAutomobileModelGroupKey(brand.slug, m);
      if (!groups.has(key)) {
        groups.set(key, []);
        order.push(key);
      }
      groups.get(key)!.push(m);
    }
    return order
      .map((k) => ({
        key: k,
        name: normalizeAutomobileModelDisplayName(brand.slug, groups.get(k)![0].name),
        models: groups.get(k)!,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' }));
  }, [brand, filteredModels]);

  const bodyCounts = useMemo(() => {
    if (!brand) return {} as Partial<Record<AutomobileBodyType, number>>;
    return brand.models.reduce(
      (acc, m) => {
        acc[m.bodyType] = (acc[m.bodyType] || 0) + 1;
        return acc;
      },
      {} as Partial<Record<AutomobileBodyType, number>>
    );
  }, [brand]);

  usePageSeo(
    brand
      ? {
          title: brand.name,
          description: brand.description.slice(0, 160) || `${brand.name} otomobil modelleri.`,
          keywords: `${brand.name}, otomobil, model`,
          noindex: true,
        }
      : slug
        ? {
            title: slug,
            description: 'Otomobil marka sayfası',
            keywords: 'otomobil, marka',
            noindex: true,
          }
        : null,
    [brand?.slug, slug],
  );

  if (loading) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Yükleniyor…</p>
        </div>
      </div>
    );
  }

  if (!brand && error) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <Link
            to="/otomobil"
            className="inline-flex items-center gap-2 text-sm text-indigo-500 hover:underline mb-6"
          >
            <ArrowLeft size={16} aria-hidden />
            Marka listesi
          </Link>
          <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
            Katalog yüklenemedi. Lütfen{' '}
            <Link to="/otomobil" className="text-indigo-500 hover:underline">
              marka listesinden
            </Link>{' '}
            tekrar deneyin.
          </p>
        </div>
      </div>
    );
  }

  if (catalogReady && !brand) {
    return <Navigate to="/otomobil" replace />;
  }

  const card = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm';
  const text = darkMode ? 'text-gray-300' : 'text-gray-600';
  const heading = darkMode ? 'text-white' : 'text-gray-900';
  const countryLabel = formatAutomobileCountry(brand.country) || null;

  const statusLabel = brand.companyStatus
    ? AUTOMOBILE_COMPANY_STATUS_LABELS[brand.companyStatus]
    : null;

  const statusBadgeClass =
    brand.companyStatus === 'defunct'
      ? darkMode
        ? 'border-red-500/40 bg-red-500/10 text-red-200'
        : 'border-red-200 bg-red-50 text-red-800'
      : brand.companyStatus === 'merged'
        ? darkMode
          ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
          : 'border-amber-200 bg-amber-50 text-amber-800'
        : brand.companyStatus === 'active' || brand.companyStatus === 'subsidiary'
          ? darkMode
            ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
            : 'border-emerald-200 bg-emerald-50 text-emerald-800'
          : darkMode
            ? 'border-gray-600 text-gray-300'
            : 'border-gray-200 text-gray-600';

  const verificationLabel =
    brand.verificationLevel === 'verified'
      ? 'Doğrulanmış'
      : brand.verificationLevel === 'partial'
        ? 'Kısmen doğrulanmış'
        : 'Henüz doğrulanmadı';

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <Link
          to="/otomobil"
          className={`inline-flex items-center gap-2 mb-6 text-sm ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
        >
          <ArrowLeft size={18} />
          Tüm markalar
        </Link>

        <header className={`rounded-2xl border p-6 sm:p-8 mb-6 ${card}`}>
          <div className="flex flex-col sm:flex-row sm:items-start gap-5">
            <AutomobileBrandLogo
              brand={brand}
              darkMode={darkMode}
              className="w-[160px] h-[72px] sm:w-[200px] sm:h-[88px]"
              iconSize={32}
            />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <h1 className={`text-2xl sm:text-3xl font-bold ${heading}`}>{brand.name}</h1>
                {countryLabel && (
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs ${
                      darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-200 text-gray-600'
                    }`}
                  >
                    <Globe size={12} />
                    {countryLabel}
                  </span>
                )}
                {statusLabel && (
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs ${statusBadgeClass}`}
                  >
                    <Building2 size={12} />
                    {statusLabel}
                  </span>
                )}
              </div>
              <p className={`text-sm sm:text-base leading-relaxed ${text}`}>{brand.description}</p>

              <dl className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3 text-sm">
                {brand.founded && (
                  <div className={`flex items-center gap-2 ${text}`}>
                    <Calendar size={16} className="shrink-0 text-indigo-500" />
                    <span>
                      <dt className="sr-only">Kuruluş</dt>
                      <dd>Kuruluş: {brand.founded}</dd>
                    </span>
                  </div>
                )}
                {brand.headquarters && (
                  <div className={`flex items-center gap-2 ${text}`}>
                    <MapPin size={16} className="shrink-0 text-indigo-500" />
                    <span>
                      <dt className="sr-only">Merkez</dt>
                      <dd>{brand.headquarters}</dd>
                    </span>
                  </div>
                )}
                {brand.website && (
                  <div className={`flex items-center gap-2 ${text}`}>
                    <ExternalLink size={16} className="shrink-0 text-indigo-500" />
                    <dd>
                      <a
                        href={brand.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-500 hover:underline break-all"
                      >
                        Resmi site
                      </a>
                    </dd>
                  </div>
                )}
              </dl>

              {brand.highlights && brand.highlights.length > 0 && (
                <ul className="mt-4 flex flex-wrap gap-2">
                  {brand.highlights.map((item) => (
                    <li
                      key={item}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs ${
                        darkMode
                          ? 'border-indigo-500/30 bg-indigo-500/10 text-indigo-200'
                          : 'border-indigo-200 bg-indigo-50 text-indigo-800'
                      }`}
                    >
                      <Sparkles size={12} />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </header>

        <section className={`rounded-2xl border p-4 sm:p-6 mb-6 ${card}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className={`text-lg font-semibold ${heading}`}>Şirket bilgileri</h2>
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs ${
                brand.verificationLevel === 'verified'
                  ? darkMode
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                    : 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : brand.verificationLevel === 'partial'
                    ? darkMode
                      ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                      : 'border-amber-200 bg-amber-50 text-amber-800'
                    : darkMode
                      ? 'border-gray-600 text-gray-400'
                      : 'border-gray-200 text-gray-500'
              }`}
            >
              <ShieldCheck size={12} />
              {verificationLabel}
            </span>
          </div>

          <dl className="grid gap-3 sm:grid-cols-2 text-sm">
            {brand.legalName && (
              <div>
                <dt className={`text-xs uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Resmi / yasal ad
                </dt>
                <dd className={`mt-0.5 font-medium ${heading}`}>{brand.legalName}</dd>
              </div>
            )}
            {brand.parentCompany && (
              <div>
                <dt className={`text-xs uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Ana şirket / grup
                </dt>
                <dd className={`mt-0.5 ${text}`}>{brand.parentCompany}</dd>
              </div>
            )}
            {brand.closedYear && (
              <div>
                <dt className={`text-xs uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Kapanış yılı
                </dt>
                <dd className={`mt-0.5 ${text}`}>{brand.closedYear}</dd>
              </div>
            )}
            {brand.successorBrand && (
              <div>
                <dt className={`text-xs uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Devam eden marka
                </dt>
                <dd className={`mt-0.5 ${text}`}>{brand.successorBrand}</dd>
              </div>
            )}
          </dl>

          {brand.closureReason && (
            <div className={`mt-4 p-3 rounded-xl border text-sm ${darkMode ? 'border-red-500/30 bg-red-500/5 text-red-100' : 'border-red-100 bg-red-50 text-red-900'}`}>
              <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                Kapanma / birleşme nedeni
              </p>
              <p>{brand.closureReason}</p>
            </div>
          )}

          {brand.verificationLevel === 'unverified' && (
            <p className={`mt-4 text-sm ${text}`}>
              Bu marka için resmi şirket kaydı henüz doğrulanmadı. Bilgiler zamanla eklenecektir.
            </p>
          )}

          {brand.sources && brand.sources.length > 0 && (
            <div className="mt-4">
              <p className={`text-xs uppercase tracking-wide mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Kaynaklar
              </p>
              <ul className="flex flex-wrap gap-2">
                {brand.sources.map((source) => (
                  <li key={source.url}>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs hover:underline ${
                        darkMode
                          ? 'border-gray-600 text-gray-300 hover:text-white'
                          : 'border-gray-200 text-gray-700 hover:text-gray-900'
                      }`}
                    >
                      <ExternalLink size={12} />
                      {source.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <section className={`rounded-2xl border p-4 sm:p-6 ${card}`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div>
              <h2 className={`text-lg sm:text-xl font-semibold ${heading}`}>Modeller</h2>
              <p className={`text-xs sm:text-sm mt-1 ${text}`}>
                {brand.models.length > 0
                  ? `${brand.models.length} model · gövde tipine göre filtreleyin`
                  : 'Bu marka için henüz model eklenmedi.'}
              </p>
            </div>
          </div>

          {brand.models.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-5">
              <button
                type="button"
                onClick={() => setBodyFilter('')}
                className={`px-2.5 py-1 rounded-lg border text-xs ${
                  bodyFilter === ''
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : darkMode
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Tümü ({brand.models.length})
              </button>
              {AUTOMOBILE_BODY_TYPE_ORDER.filter((t) => bodyCounts[t]).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setBodyFilter(type)}
                  className={`px-2.5 py-1 rounded-lg border text-xs ${
                    bodyFilter === type
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : `${AUTOMOBILE_BODY_CHIP[type]} hover:opacity-90`
                  }`}
                >
                  {AUTOMOBILE_BODY_TYPE_LABELS[type]} ({bodyCounts[type]})
                </button>
              ))}
            </div>
          )}

          {filteredModels.length > 0 ? (
            <div className="space-y-6">
              {groupedModels.map((g) => (
                <details
                  key={g.key}
                  open
                  className={`rounded-xl border ${
                    darkMode ? 'border-gray-700 bg-gray-900/30' : 'border-gray-200 bg-white/60'
                  }`}
                >
                  <summary
                    className={`cursor-pointer select-none px-4 py-3 flex items-center justify-between gap-3 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}
                  >
                    <span className="font-semibold truncate">{g.name}</span>
                    <span
                      className={`shrink-0 text-xs px-2 py-0.5 rounded-md border ${
                        darkMode ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-gray-600'
                      }`}
                      title="Bu model ailesindeki varyant sayısı (yıl/nesil)"
                    >
                      {g.models.length} varyant
                    </span>
                  </summary>
                  <div className="px-4 pb-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {g.models
                        .slice()
                        .sort((a, b) => {
                          const ay = a.yearFrom ?? 0;
                          const by = b.yearFrom ?? 0;
                          if (ay !== by) return by - ay;
                          return (a.displayName || a.name).localeCompare(b.displayName || b.name, 'tr', {
                            sensitivity: 'base',
                          });
                        })
                        .map((model, idx) => (
                          <AutomobileModelCard
                            key={getAutomobileModelKey(model, idx)}
                            model={model}
                            brandName={brand.name}
                            darkMode={darkMode}
                            hideBaseName
                          />
                        ))}
                    </div>
                  </div>
                </details>
              ))}
            </div>
          ) : brand.models.length === 0 ? (
            <p className={`text-sm ${text}`}>
              Model verisi yakında eklenecek. Şimdilik marka profili görüntüleniyor.
            </p>
          ) : (
            <p className={`text-sm ${text}`}>Seçilen gövde tipinde model bulunamadı.</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default AutomobileBrandPage;
